import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { Box, Typography, CircularProgress, Alert } from '@mui/material';
import oauthService from '../../services/oauthService';

const OAuthCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('processing');
  const [message, setMessage] = useState('Completing OAuth connection...');
  // Public-client flows (e.g. Lightspeed R-Series) have no SuperChargeMe login, so we
  // must NOT auto-navigate to a private page after success. Gated per-platform below.
  const [willRedirect, setWillRedirect] = useState(true);

  useEffect(() => {
    const handleCallback = async () => {
      try {
        // Determine platform from URL path
        const pathParts = location.pathname.split('/');
        const platformIndex = pathParts.indexOf('oauth') + 1;
        const platform = pathParts[platformIndex];
        
        if (!platform) {
          setStatus('error');
          setMessage('Invalid OAuth callback URL');
          return;
        }

        setMessage(`Completing ${oauthService.getPlatformName(platform)} connection...`);

        // Get OAuth parameters from URL
        const oauthParams = {};
        for (const [key, value] of searchParams) {
          oauthParams[key] = value;
        }

        // Handle different platform callback requirements
        let requiredParams = [];
        switch (platform) {
          case 'shopify':
            requiredParams = ['code', 'state', 'shop'];
            break;
          case 'facebook':
            requiredParams = ['code', 'state'];
            break;
          case 'amazon-ads':
            requiredParams = ['code', 'state'];
            break;
          case 'amazon-sp':
            requiredParams = ['spapi_oauth_code', 'state', 'selling_partner_id'];
            break;
          case 'google-ads':
            requiredParams = ['code', 'state'];
            break;
          case 'tiktok':
            requiredParams = ['state']; // TikTok sends both auth_code and code, just need one
            break;
          case 'tiktok-creator':
            requiredParams = ['code', 'state']; // Login Kit from developers.tiktok.com
            break;
          case 'tiktok-shop':
          case 'tiktokshop': // non-hyphen alias — Partner Center redirect path
            requiredParams = ['code', 'state'];
            break;
          case 'slack':
            requiredParams = ['code', 'state'];
            break;
          case 'teams':
          case 'microsoft-teams':
            requiredParams = ['code', 'state'];
            break;
          case 'klaviyo':
            requiredParams = ['code', 'state'];
            break;
          case 'pinterest':
            requiredParams = ['code', 'state'];
            break;
          case 'google-search-console':
            requiredParams = ['code', 'state'];
            break;
          case 'google-analytics':
            requiredParams = ['code', 'state'];
            break;
          case 'google-merchant-center':
            requiredParams = ['code', 'state'];
            break;
          case 'google-postmaster':
            requiredParams = ['code', 'state'];
            break;
          case 'quickbooks':
            requiredParams = ['code', 'state', 'realmId'];
            break;
          case 'xero':
            requiredParams = ['code', 'state'];
            break;
          case 'revolut':
            requiredParams = ['code', 'state'];
            break;
          case 'microsoft-ads':
            requiredParams = ['code', 'state'];
            break;
          case 'attentive':
            requiredParams = ['code', 'state'];
            break;
          case 'google-workspace':
            requiredParams = ['code', 'state'];
            break;
          case 'microsoft-workspace':
            requiredParams = ['code', 'state'];
            break;
          case 'sps-commerce':
            requiredParams = ['code', 'state'];
            break;
          case 'lightspeed': // Lightspeed Retail (R-Series) OAuth
            requiredParams = ['code', 'state'];
            break;
          case 'square':
            requiredParams = ['code', 'state'];
            break;
          case 'business-central': // sign-in returns code + state; the admin consent return has state + admin_consent only
            requiredParams = ['state'];
            break;
          case 'snapchat-ads':
            requiredParams = ['code', 'state'];
            break;
          case 'reddit-ads':
            requiredParams = ['code', 'state'];
            break;
          case 'sa360':
            requiredParams = ['code', 'state'];
            break;
          case 'amazon-vendor': // SP-API website authorization redirect (Vendor Central)
            requiredParams = ['state', 'selling_partner_id', 'spapi_oauth_code'];
            break;
          case 'dv360':
          case 'cm360':
            requiredParams = ['code', 'state'];
            break;
          default:
            throw new Error(`Unsupported platform: ${platform}`);
        }

        // Check if all required parameters are present
        const missingParams = requiredParams.filter(param => !oauthParams[param]);
        
        // Special case for TikTok - ensure we have either auth_code or code
        if (platform === 'tiktok') {
          if (!oauthParams.auth_code && !oauthParams.code) {
            setStatus('error');
            setMessage('Missing OAuth authorization code from TikTok');
            return;
          }
        } else if (missingParams.length > 0) {
          setStatus('error');
          setMessage(`Missing OAuth parameters: ${missingParams.join(', ')}`);
          return;
        }

        const result = await oauthService.handleCallback(platform, oauthParams);
        
        if (result.success) {
          // Handle special cases first
          if (result.redirect_url) {
            // Resource selection flow
            setStatus('success');
            setMessage(`OAuth successful! Redirecting to resource selection...`);
            setTimeout(() => {
              // For QuickBooks/Xero, append temp_token_id to redirect URL
              let redirectUrl = result.redirect_url;
              if ((platform === 'quickbooks' || platform === 'xero') && result.temp_token_id) {
                redirectUrl += `?temp_token_id=${result.temp_token_id}`;
              }
              navigate(redirectUrl);
            }, 1000);
          } else if (result.pendingSetup) {
            // Backend completed auth but deferred connector creation (e.g., missing state)
            const shopName = result.data?.shopInfo?.name || result.data?.shop || 'your account';
            const platformName = oauthService.getPlatformName(platform);
            setStatus('success');
            setMessage(`${platformName} authenticated for ${shopName}. Please finish setup in the app.`);
            setTimeout(() => {
              navigate('/connectors');
            }, 2000);
          } else {
            // Traditional connector creation flow
            const connectorName = result.connector?.name || oauthService.getPlatformName(platform);
            setStatus('success');
            if (platform === 'lightspeed' || result.shared_link === true) {
              // Public client flow — the person authorizing has no SuperChargeMe login, so do
              // NOT navigate to a private page (/connectors is a PrivateRoute and would dump
              // them at /login, reading as a failure even though the connector was created).
              // lightspeed is always this flow; `shared_link` is set by the backend when the
              // OAuth state came from a "Copy client link" press (Amazon Ads / Amazon SP).
              setWillRedirect(false);
              setMessage(`${connectorName} connected. You can close this tab — we'll finish setting up the data sync on our end.`);
            } else {
              setMessage(`Successfully connected ${connectorName}!`);
              setTimeout(() => {
                navigate('/connectors');
              }, 2000);
            }
          }
        } else {
          setStatus('error');
          setMessage(result.message || 'OAuth connection failed');
        }

      } catch (error) {
        console.error('OAuth callback error:', error);
        setStatus('error');
        setMessage(error.message || 'Failed to complete OAuth connection');
      }
    };

    handleCallback();
  }, [location.pathname, searchParams, navigate]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '60vh',
        textAlign: 'center',
        p: 3
      }}
    >
      {status === 'processing' && (
        <>
          <CircularProgress size={60} sx={{ mb: 3 }} />
          <Typography variant="h5" gutterBottom>
            {message}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Please wait while we complete the setup
          </Typography>
        </>
      )}

      {status === 'success' && (
        <>
          <Alert severity="success" sx={{ mb: 3, minWidth: 400 }}>
            <Typography variant="h6" gutterBottom>
              Connection Successful!
            </Typography>
            <Typography variant="body2">
              {message}
            </Typography>
          </Alert>
          {willRedirect && (
            <Typography variant="body2" color="text.secondary">
              Redirecting to connectors page...
            </Typography>
          )}
        </>
      )}

      {status === 'error' && (
        <>
          <Alert severity="error" sx={{ mb: 3, minWidth: 400 }}>
            <Typography variant="h6" gutterBottom>
              Connection Failed
            </Typography>
            <Typography variant="body2">
              {message}
            </Typography>
          </Alert>
          <Typography variant="body2" color="text.secondary">
            You can try again or contact support if the problem persists.
          </Typography>
        </>
      )}
    </Box>
  );
};

export default OAuthCallback;